import type {
  WholesaleHero,
  WholesaleIntro,
  WholesaleAccordionSection,
  WholesaleFAQItem,
  WholesaleGalleryImage,
} from './types'

/**
 * V2 wholesale page — content reused 1:1 from the original WholesalePage
 * to fully preserve existing SEO signals (H1 keywords, body copy, FAQs).
 */

export const wholesaleHero: WholesaleHero = {
  image:
    'https://static.lumi.new/b7/b770205c176c240c2cffd6f5fcc129af.png',
  imageAlt: {
    zh: 'ONGU 商業豆批發｜營業用咖啡豆',
    en: 'ONGU Wholesale Coffee Beans for Business',
  },
  title: {
    zh: 'Wholesale',
    en: 'Wholesale',
  },
  h1: {
    zh: '商業豆批發｜營業用咖啡豆專業供應',
    en: 'Wholesale Coffee Beans｜Professional Commercial Supply',
  },
}

export const wholesaleIntro: WholesaleIntro = {
  image:
    'https://static.lumi.new/b7/b770205c176c240c2cffd6f5fcc129af.png',
  imageAlt: {
    zh: 'ONGU Studio 烘豆現場',
    en: 'ONGU Studio Roastery',
  },
  eyebrow: {
    zh: '飲食店向けの卸売サービス',
    en: 'Wholesale for Cafés & Restaurants',
  },
  title: {
    zh: 'From Our Roastery',
    en: 'From Our Roastery',
  },
  body: {
    zh:
      'ONGU 是一家新創咖啡烘焙商，我們相信咖啡不只是商品，而是一場與合作夥伴共同成長的旅程。我們將自己視為客戶團隊的一份子，負責從全球挑選優質生豆、數據化烘焙、杯測控管到配送流程，以穩定、高品質的咖啡豆支持每一家咖啡館、餐廳、辦公室與飯店。\n\n我們持續探索更有效、更精準的烘焙方式，透過科學化曲線紀錄與風味實驗，找到每支豆子的最佳呈現。我們的目標很簡單：讓每一位合作夥伴都能安心把「招牌風味」交給 ONGU，並一起把咖啡的故事傳遞出去。',
    en:
      "ONGU is a startup coffee roaster that believes coffee is more than just a product—it's a journey of growth with our partners. We see ourselves as part of our clients' teams, handling everything from sourcing premium green beans globally, data-driven roasting, cupping quality control, to delivery logistics, supporting every cafe, restaurant, office, and hotel with stable, high-quality coffee beans.\n\nWe continuously explore more efficient and precise roasting methods through scientific curve logging and flavor experiments to find the best expression of each bean. Our goal is simple: to let every partner confidently entrust their 'signature flavor' to ONGU and share the story of coffee together.",
  },
  ctaLabel: {
    zh: '申請免費試豆 →',
    en: 'Request Free Sample →',
  },
  ctaHref: '#apply',
}

/**
 * Five expandable sections — content kept identical to the original
 * WholesalePage so the SEO body copy is fully preserved.
 */
export const wholesaleSections: WholesaleAccordionSection[] = [
  {
    title: {
      zh: '為什麼商業豆要選 ONGU Coffee？',
      en: 'Why Choose ONGU Coffee for Commercial Beans?',
    },
    body: {
      zh:
        '商業豆最重要的是「穩定」與「好出杯」。ONGU Coffee 以自家烘焙與實際門市實戰經驗，為營業用咖啡設計多款穩定配方：萃取容錯率高、不挑機器、不挑手法，讓前台吧檯可以專注服務，不用每天為風味波動煩惱。',
      en:
        "The most important aspects of commercial beans are 'stability' and 'easy extraction'. ONGU Coffee designs multiple stable formulas for commercial coffee based on in-house roasting and actual store experience: high extraction tolerance, machine-agnostic, technique-agnostic, allowing front-line baristas to focus on service without worrying about flavor fluctuations every day.",
    },
  },
  {
    title: {
      zh: '合作店家與品牌夥伴',
      en: 'Partner Stores & Brand Collaborations',
    },
    body: {
      zh:
        'ONGU Coffee 長期為台北精品咖啡廳、信義區餐酒館、風格旅宿與企業內部咖啡吧提供穩定的營業用咖啡豆。每一位合作夥伴都有不同的品牌定位與客群需求，我們從選豆、烘焙、配方設定到供貨週期，全程配合你的節奏與規格，讓你不需要從頭摸索就能端出一杯對的咖啡。',
      en:
        'ONGU Coffee supplies stable commercial coffee beans to specialty cafes, wine bars in Xinyi District, boutique hotels, and corporate coffee bars across Taipei. Every partner has a unique brand and clientele — we tailor sourcing, roasting, blend formulation, and delivery schedules to your exact pace and spec, so you can serve the right cup from day one.',
    },
  },
  {
    title: {
      zh: 'ONGU STUDIO 現場咖啡豆',
      en: 'ONGU STUDIO On-Site Coffee Beans',
    },
    body: {
      zh:
        '所有商業豆都回到 ONGU Studio 的現場烘豆流程嚴格控管。從生豆挑選、水洗／日曬處理方式、烘焙曲線設定，到每一批次的杯測紀錄，都完整記錄於工作室系統中。合作店家也可以預約來到 ONGU Studio，親眼看見自己的商業豆如何被烘焙出爐，真正做到「看得見的專業」。',
      en:
        "All commercial beans are strictly controlled through ONGU Studio's on-site roasting process. From green bean selection, washed/natural processing methods, roast curve settings, to cupping records for each batch, everything is fully recorded in the studio system. Partner stores can also book a visit to ONGU Studio to see firsthand how their commercial beans are roasted, truly achieving 'visible professionalism'.",
    },
  },
  {
    title: {
      zh: '商業配方與風味客製',
      en: 'Custom Blends & Flavor Development',
    },
    body: {
      zh:
        '無論你需要的是「適合拿鐵的濃郁堅果感」、「適合甜點的果酸調性」，或是希望延伸出專屬於品牌的 Signature Blend，ONGU Coffee 都能透過杯測與多批次測試，協助調整配方比例與烘焙度，打造只屬於你店裡的招牌風味。',
      en:
        "Whether you need 'rich nutty notes for lattes', 'fruity acidity for desserts', or want to develop a brand-exclusive Signature Blend, ONGU Coffee can assist in adjusting blend ratios and roast profiles through cupping and multiple batch testing, creating a signature flavor unique to your store.",
    },
  },
  {
    title: {
      zh: '營業用供貨模式與配送說明',
      en: 'Commercial Supply Model & Delivery Information',
    },
    body: {
      zh:
        '我們提供固定週期出貨與彈性訂購兩種供貨模式，台灣本島皆可宅配，到貨時間約 1～2 個工作天（不含週日與國定假日）。每批商業豆皆有烘焙日期標示，建議於 7–21 天熟成期內使用，確保客人喝到最佳風味。',
      en:
        'We offer both fixed-cycle delivery and flexible ordering models. Delivery is available throughout Taiwan mainland, with delivery time approximately 1-2 business days (excluding Sundays and national holidays). Each batch of commercial beans has a roast date label, recommended for use within 7-21 days aging period to ensure customers enjoy the best flavor.',
    },
  },
]

export const wholesaleFAQ: WholesaleFAQItem[] = [
  {
    q: {
      zh: 'Q1｜商業合作的最低訂購量是多少？',
      en: 'Q1｜What is the minimum order quantity for commercial partnerships?',
    },
    a: {
      zh:
        'A：建議從 3kg 起試用，讓你有足夠量測試穩定度與客人反應。確認風味方向後，我們再依照你的實際銷量規劃固定供貨週期，沒有強制門檻，以適合你的量開始。',
      en:
        "A: We recommend starting from 3kg — enough to test extraction stability and get real feedback from your customers. Once the flavor direction is confirmed, we'll set up a recurring supply cycle that fits your actual sales volume. No forced minimums; start at whatever scale makes sense for you.",
    },
  },
  {
    q: {
      zh: 'Q2｜可以先少量試用或杯測嗎？',
      en: 'Q2｜Can I try samples or cupping first?',
    },
    a: {
      zh:
        'A：可以，這是我們建議的第一步。填表申請後，我們會寄出 200g × 3 款樣品，你可以在自己的機器與配方下實際測試萃取表現，確認穩定性再決定是否合作，完全不用倉促下單。',
      en:
        "A: Yes — and we recommend it as the first step. After you submit a request, we'll ship 200g × 3 sample varieties for you to test on your own equipment and workflow. Evaluate extraction stability before committing. No pressure to order right away.",
    },
  },
  {
    q: {
      zh: 'Q3｜能不能設計屬於自己品牌的專屬風味？',
      en: 'Q3｜Can you design a custom flavor profile for our brand?',
    },
    a: {
      zh:
        'A：可以。我們從杯測開始，依照你的菜單定位（拿鐵導向 / 手沖導向 / 甜點搭配）討論風味方向，再經過多批次調整，直到你滿意為止。專屬配方的豆不會供給其他店家，是真正屬於你的招牌。',
      en:
        "A: Yes. We start from cupping sessions and discuss flavor direction based on your menu positioning (latte-forward, pour-over, or dessert-pairing). After multiple rounds of adjustment, we lock in a blend that truly represents your brand — and we don't supply the same formula to any other store.",
    },
  },
  {
    q: {
      zh: 'Q4｜新開店可以協助規劃豆單與吧台流程嗎？',
      en: 'Q4｜Can you help plan the bean menu and bar workflow for a new store?',
    },
    a: {
      zh:
        'A：可以，這也是我們最常做的事之一。我們協助規劃豆單品項數量、配方方向、萃取參數，以及前台動線建議，讓你在開業第一天就有清楚的咖啡 SOP，不用邊開店邊摸索。',
      en:
        "A: Yes — it's one of the most common ways we support new partners. We help define menu size, blend direction, extraction parameters, and front-line SOP recommendations, so you walk into opening day with a clear coffee workflow instead of figuring it out mid-service.",
    },
  },
]

/** Full-bleed slide gallery — reuses existing assets in repo. */
export const wholesaleGallery: WholesaleGalleryImage[] = [
  {
    src: 'https://static.lumi.new/b7/b770205c176c240c2cffd6f5fcc129af.png',
    caption: { zh: '烘豆現場', en: 'Roasting Floor' },
  },
  {
    src: 'https://static.lumi.new/e7/e72c8c67a328deeff79ab1b635db3e54.jpeg',
    caption: { zh: '杯測紀錄', en: 'Cupping Session' },
  },
  {
    src: 'https://static.lumi.new/37/3773765a3ea8df62f0d98d9e5f899b4f.png',
    caption: { zh: '配方研發', en: 'Blend Development' },
  },
  {
    src: 'https://static.lumi.new/b7/b770205c176c240c2cffd6f5fcc129af.png',
    caption: { zh: '出貨包裝', en: 'Packaging & Delivery' },
  },
]
